import { roles } from '../../../util/constants';
import useShortCode from '../../../hooks/useShortCode';
import useUserPermissions from '../../../hooks/useUserPermissions';

/**
 * Check if a user has any of the required roles
 * @param {Object} user - The user object
 * @param {Array|string} requiredRoles - Role(s) to check for
 * @param {string} orgShortCode - Organization short code
 * @returns {boolean} - True if user has any required role
 */
function hasRole(user, requiredRoles, orgShortCode) {
  const rolesToCheck = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];

  if (!user?.permissions) {
    return false;
  }

  if (
    user.permissions.global &&
    Array.isArray(user.permissions.global) &&
    user.permissions.global.includes(roles.admin)
  ) {
    return true;
  }

  if (
    user.permissions.global &&
    Array.isArray(user.permissions.global) &&
    rolesToCheck.some(role => user.permissions.global.includes(role))
  ) {
    return true;
  }

  const orgPermissions = user.permissions.organisation?.find(
    org => org.orgShortCode === orgShortCode,
  );

  if (
    orgPermissions?.roles &&
    Array.isArray(orgPermissions.roles) &&
    rolesToCheck.some(role => orgPermissions.roles.includes(role))
  ) {
    return true;
  }

  return false;
}

const getRoles = (user, shortCode) => {
  const organisationRoles =
    user?.permissions?.organisation?.find(org => org.orgShortCode === shortCode)?.roles || [];
  const globalRoles = user?.permissions?.global || [];
  return { organisationRoles, globalRoles };
};

/**
 * RoleGate component that conditionally renders children based on user roles
 *
 * @param {Object} props - Component props
 * @param {string|string[]} props.roles - Required role(s) to access the content
 * @param {React.ReactNode|Function} props.children - Content to render if user has required role(s)
 * @param {React.ReactNode|Function} props.renderGate - Content to render if user lacks required role(s)
 * @param {boolean} props.inverse - If true, renders children when user does NOT have the required role(s)
 * @param {string} props.type - Type of gate behavior: 'disabled' disables children when not allowed
 * @returns {React.ReactElement|null}
 */
const RoleGate = ({
  roles = [],
  children,
  user: userProp = null,
  renderGate = null,
  inverse = false,
  override = false,
  type = null,
}) => {
  const userPermissions = useUserPermissions();
  const shortCode = useShortCode();

  const user = userProp || userPermissions;
  const { organisationRoles, globalRoles } = getRoles(user, shortCode);

  const loading = userPermissions.loading;
  const hasPermissionsData = user && user.permissions;
  const isAllowed = hasRole(user, roles, shortCode);
  const shouldRenderContent = inverse ? !isAllowed : isAllowed;

  if (loading || !hasPermissionsData) return null;

  if (shouldRenderContent || override) {
    return typeof children === 'function'
      ? children({ isAllowed, organisationRoles, globalRoles })
      : children;
  }

  if (type === 'disabled') {
    return (
      <div
        style={{
          opacity: 0.5,
          pointerEvents: 'none',
          cursor: 'not-allowed',
        }}
      >
        {typeof children === 'function'
          ? children({ isAllowed, organisationRoles, globalRoles })
          : children}
      </div>
    );
  }

  if (!isAllowed) {
    return typeof renderGate === 'function'
      ? renderGate({ isAllowed, organisationRoles, globalRoles }, children)
      : renderGate;
  }

  return null;
};

export default RoleGate;
export { getRoles, hasRole };
